/* import __COLOCATED_TEMPLATE__ from './ecommerce-performance.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { HEIGHT_TYPES, WIDTH_TYPES } from 'embercom/models/reporting/custom/chart';
import { VISUALIZATION_TYPES } from 'embercom/models/reporting/custom/visualization-options';
import { tracked } from '@glimmer/tracking';
import PALETTE from '@intercom/pulse/lib/palette';
import { filterableProperties as defaultFilterableProperties } from 'embercom/lib/reporting/custom/filter-helpers';
import type ConversationAttributeDescriptor from 'embercom/models/conversation-attributes/descriptor';
import { createRoleScopedFunnelConfig } from 'embercom/components/reporting/bespoke/fin-performance/conversation-funnel/series-config';

const DEFAULT_CHART_DEFINITION = {
  id: null,
  title: null,
  visualization_type: null,
  metric_property: null,
  metric_function: null,
  stacked: false,
  view_by: null,
  view_by_time_interval: null,
  view_by_display_limit: 10,
  view_by_display_other: false,
  width_type: null,
  height_type: null,
};

const CHART_SERIES_COLORS = {
  finAutomated: PALETTE['vis-orange-50'],
  finInvolved: PALETTE['vis-blue-50'],
  finResolved: PALETTE['vis-mint-50'],
  customerExperience: PALETTE['vis-violet-50'],
};

interface Signature {
  Args: {
    conversationAttributeDescriptors: ConversationAttributeDescriptor[];
  };
}

export default class EcommercePerformance extends Component<Signature> {
  @service declare appService: AppService;
  @service declare intl: IntlService;

  @tracked impactOverTimeMetricConfig = 'percentage';
  @tracked isNarrowScreen = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.setupResizeListener();
    this.handleResize();
  }

  willDestroy() {
    super.willDestroy();
    this.removeResizeListener();
  }

  get finEcommerceParticipatedFilter() {
    return {
      type: 'category' as const,
      data: {
        property: 'fin_ecommerce.participated',
        attribute: 'conversation.fin_ecommerce.participated',
        values: [true],
      },
    };
  }

  private get participatedFilterSeries() {
    return { filters: { type: 'and' as const, filters: [this.finEcommerceParticipatedFilter] } };
  }

  get serializedReport() {
    return {
      id: 'fin-ecommerce-performance',
      title: null,
      description: null,
      charts: [],
      filters: {},
      date_range: {
        selection: 'past_4_weeks',
      },
    };
  }

  get filterableProperties() {
    if (this.appService.app.isStandaloneApp) {
      return [];
    }

    return defaultFilterableProperties().concat(
      'teammate',
      'team',
      'conversation.replied.workflow_ids',
    );
  }

  get performanceOverTimeBaseChart() {
    return {
      ...DEFAULT_CHART_DEFINITION,
      id: 'fin_ecommerce_performance_over_time',
      visualization_type: VISUALIZATION_TYPES.BESPOKE,
      component_name: 'fin-performance/performance-over-time',
      width_type: WIDTH_TYPES.FULL,
      height_type: HEIGHT_TYPES.TALL,
      view_by: 'time',
      show_default_chart_title: false,
    };
  }

  get absolutePerformanceOverTimeChart() {
    return [
      {
        ...this.performanceOverTimeBaseChart,
        chart_series: [
          {
            metric_id: 'v1.new_conversations',
            label: this.intl.t('ai-agent.analyze.performance.new-conversations'),
            color: CHART_SERIES_COLORS.finAutomated,
            ...this.participatedFilterSeries,
          },
          {
            metric_id: 'fin.conversations_participated.count',
            label: this.intl.t('ai-agent.analyze.performance.fin-involvements'),
            color: CHART_SERIES_COLORS.finInvolved,
            ...this.participatedFilterSeries,
          },
          {
            metric_id: 'fin.conversations_resolved.count',
            label: this.intl.t('ai-agent.analyze.performance.fin-resolutions'),
            color: CHART_SERIES_COLORS.finResolved,
            ...this.participatedFilterSeries,
          },
          {
            metric_id: 'conversation.ai_generated_metrics.csat.positively_rated.count',
            label: this.intl.t('ai-agent.analyze.performance.positive-fin-experiences'),
            filters: {
              type: 'and',
              filters: [this.finEcommerceParticipatedFilter],
            },
            color: CHART_SERIES_COLORS.customerExperience,
          },
        ],
      },
    ];
  }

  get percentagePerformanceOverTimeChart() {
    return [
      {
        ...this.performanceOverTimeBaseChart,
        chart_series: [
          {
            metric_id: 'fin_ecommerce.automation_rate',
            label: this.intl.t('ai-agent.analyze.performance.automation-rate'),
            color: CHART_SERIES_COLORS.finAutomated,
          },
          {
            metric_id: 'fin_ecommerce.participation_rate',
            label: this.intl.t('ai-agent.analyze.performance.involvement-rate'),
            color: CHART_SERIES_COLORS.finInvolved,
          },
          {
            metric_id: 'fin_ecommerce.resolution_rate',
            label: this.intl.t('ai-agent.analyze.performance.resolution-rate'),
            color: CHART_SERIES_COLORS.finResolved,
          },
          {
            metric_id:
              'conversation.ai_generated_metrics.csat_score.first_user_conversation_part_created_at',
            label: this.intl.t('ai-agent.analyze.performance.cx-score'),
            filters: {
              type: 'and',
              filters: [this.finEcommerceParticipatedFilter],
            },
            color: CHART_SERIES_COLORS.customerExperience,
          },
        ],
      },
    ];
  }

  get conversationFunnelChart() {
    let funnelConfig = createRoleScopedFunnelConfig('fin_ecommerce.participated');
    return {
      ...DEFAULT_CHART_DEFINITION,
      id: 'fin_ecommerce_performance.conversation_funnel',
      title: this.intl.t('reporting.overview.charts.handling.funnel.title'),
      show_default_chart_title: false,
      description: this.intl.t('reporting.overview.charts.handling.funnel.description'),
      chart_series: funnelConfig.getChartSeriesWithLabels(this.intl),
      width_type: WIDTH_TYPES.FULL,
      height_type: HEIGHT_TYPES.X_TALL,
      visualization_type: VISUALIZATION_TYPES.BESPOKE,
      component_name: 'fin-performance/conversation-funnel',
    };
  }

  get carouselServedChart() {
    return {
      ...DEFAULT_CHART_DEFINITION,
      id: 'fin_ecommerce.carousel_served',
      title: this.intl.t('ai-agent.analyze.ecommerce-performance.carousel-served'),
      visualization_type: VISUALIZATION_TYPES.DONUT,
      width_type: WIDTH_TYPES.TWO_COLUMN,
      height_type: HEIGHT_TYPES.TALL,
      view_by: 'conversation_custom_fields#ecommerce_agent_carousel_served',
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          show_data_labels: true,
          ...this.participatedFilterSeries,
        },
      ],
    };
  }

  get checkoutClickedChart() {
    return {
      ...DEFAULT_CHART_DEFINITION,
      id: 'fin_ecommerce.checkout_link_clicked',
      title: this.intl.t('ai-agent.analyze.ecommerce-performance.checkout-clicked'),
      visualization_type: VISUALIZATION_TYPES.DONUT,
      width_type: WIDTH_TYPES.TWO_COLUMN,
      height_type: HEIGHT_TYPES.TALL,
      view_by: 'conversation_custom_fields#checkout_link_clicked',
      chart_series: [
        {
          metric_id: 'v1.new_conversations',
          show_data_labels: true,
          ...this.participatedFilterSeries,
        },
      ],
    };
  }

  @action
  setImpactOverTimeMetricConfig(value: string) {
    this.impactOverTimeMetricConfig = value;
  }

  private handleResize = () => {
    this.isNarrowScreen = window.innerWidth < 1_440;
  };

  private setupResizeListener() {
    window.addEventListener('resize', this.handleResize);
  }

  private removeResizeListener() {
    window.removeEventListener('resize', this.handleResize);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAutomation::Analyze::EcommercePerformance': typeof EcommercePerformance;
    'ai-automation/analyze/ecommerce-performance': typeof EcommercePerformance;
  }
}
